import React, { forwardRef, useEffect, useRef, useCallback } from 'react';
import { useBreakpoint, useHover } from '@landr/maestro';
import { t } from '@lingui/macro';
import { Pack, RePack } from 'types';
import { EntityType, PackType } from 'types/enums';
import Card from 'components/Card/Card';
import { PlayerProvider } from 'contexts/PlayerContext/PlayerContext';
import { getStreamEntityType } from 'helpers';
import { analytics } from 'utils/analytics';
import {
    CtaGoal,
    CtaType,
    InteractionSource,
} from 'utils/analytics/AnalyticsV2Service.generated';
import useGetLocalizedEntityDetailsLink from 'hooks/useGetLocalizedEntityDetailsLink/useGetLocalizedEntityDetailsLink';
import { usePreviewContext } from '../contexts/PreviewContext/PreviewContext';
import useOptimisticQuery from '../contexts/PreviewContext/hooks/useOptimisticQuery';
import { PreviewShowDelay } from '../contexts/PreviewContext/constant';
import CardPlayer from './CardPlayer';

interface _SlideProps {
    isExclusive?: boolean;
    backgroundColor?: string;
    linkTo?: string;
    bylineLinkTo?: string;
    artworkUrl?: string;
    item?: Pack | RePack;
    title?: string;
    byline?: string;
    isLoading?: boolean;
    showCardPlayer?: boolean;
    onLinkClick?: React.MouseEventHandler<HTMLDivElement>;
}

export const _Slide = forwardRef<
    HTMLDivElement,
    _SlideProps & React.RefAttributes<HTMLDivElement>
>(
    (
        {
            isExclusive = false,
            backgroundColor,
            linkTo,
            bylineLinkTo,
            artworkUrl,
            item,
            title,
            byline,
            isLoading = false,
            showCardPlayer = false,
            onLinkClick,
        },
        ref,
    ) => (
        <Card
            ref={ref}
            isExclusive={isExclusive}
            backgroundColor={backgroundColor}
            linkTo={linkTo}
            bylineLinkTo={bylineLinkTo}
            isLoading={isLoading}
            onLinkClick={onLinkClick}
        >
            <Card.BackgroundImage
                imageUrl={artworkUrl}
                fallbackSrc={'/assets/fallback/artworkPlaceholder.svg'}
                imageSize={250}
                alt={item?.name ? t`Artwork of ${item?.name}` : ''}
            />
            {showCardPlayer && item && (
                <Card.Actions>
                    <PlayerProvider
                        playerId={item.guid}
                        streamEntityType={getStreamEntityType(item.type)}
                    >
                        <CardPlayer item={item} />
                    </PlayerProvider>
                </Card.Actions>
            )}
            <Card.Title>{title}</Card.Title>
            <Card.Byline>{byline}</Card.Byline>
        </Card>
    ),
);

export const useSlide = ({
    item,
    isHovered,
    hoverRef,
    showPreview,
    optimisticQuery,
    contentGroup,
    contentList,
}: {
    item?: Pack | RePack;
    isHovered: boolean;
    hoverRef: React.MutableRefObject<HTMLDivElement | null>;
    showPreview: ({
        guid,
        type,
        slideRef,
    }: {
        guid: string;
        type: PackType;
        slideRef: React.MutableRefObject<HTMLDivElement | null>;
    }) => void;
    optimisticQuery: ({ guid, type }: { guid: string; type: PackType }) => void;
    contentGroup?: string;
    contentList?: string;
}) => {
    const timerRef = useRef<number | undefined>(undefined);

    const getLocalizedEntityDetailsLink = useGetLocalizedEntityDetailsLink();

    const linkTo = getLocalizedEntityDetailsLink(item?.type, item?.slug);
    const bylineLinkTo = getLocalizedEntityDetailsLink(
        EntityType.Label,
        item?.label?.slug,
    );

    const isExclusive = item?.isExclusive as boolean;
    const artworkUrl = item?.artworkUrl as string;
    const title = item?.name as string;
    const byline = item?.label?.name as string;
    const backgroundColor = (item?.backgroundColor as string) ?? '#142739';

    useEffect(() => {
        if (item?.guid && item?.type) {
            optimisticQuery({ guid: item.guid, type: item.type });
        }
    }, [item?.guid, item?.type, optimisticQuery]);

    useEffect(() => {
        if (isHovered && item?.guid && item?.type) {
            timerRef.current = window.setTimeout(() => {
                showPreview({
                    guid: item.guid,
                    type: item.type,
                    slideRef: hoverRef,
                });
            }, PreviewShowDelay);
        }

        return () => {
            window.clearTimeout(timerRef.current);
        };
    }, [
        backgroundColor,
        isHovered,
        showPreview,
        hoverRef,
        item?.guid,
        item?.type,
    ]);

    const onLinkClick = useCallback(() => {
        analytics.trackCTAClicked({
            ctaGoal: CtaGoal.ViewDetail,
            ctaType: CtaType.PlainText,
            interactionSource: InteractionSource.CardAction,
            ctaDestination: linkTo,
            ctaContentGroup: contentGroup,
            ctaContentList: contentList,
        });
    }, [contentGroup, contentList]);

    return {
        linkTo,
        bylineLinkTo,
        title,
        byline,
        artworkUrl,
        isExclusive,
        backgroundColor,
        onLinkClick,
    };
};

export const SlideSkeleton = () => {
    return <_Slide isLoading={true} />;
};

const Slide = ({ item }: { item?: Pack | RePack }) => {
    const showCardPlayer = useBreakpoint('md');

    const [hoverRef, isHovered] = useHover(false);
    const { showPreview, contentGroup, contentList } = usePreviewContext();
    const optimisticQuery = useOptimisticQuery();

    const {
        linkTo,
        bylineLinkTo,
        artworkUrl,
        title,
        byline,
        isExclusive,
        backgroundColor,
        onLinkClick,
    } = useSlide({
        item,
        isHovered,
        showPreview,
        hoverRef,
        optimisticQuery,
        contentGroup,
        contentList,
    });

    if (!item) {
        return <SlideSkeleton />;
    }

    return (
        <_Slide
            ref={hoverRef}
            isExclusive={isExclusive}
            backgroundColor={backgroundColor}
            linkTo={linkTo}
            bylineLinkTo={bylineLinkTo}
            title={title}
            byline={byline}
            item={item}
            artworkUrl={artworkUrl}
            showCardPlayer={showCardPlayer}
            onLinkClick={onLinkClick}
        />
    );
};

export default Slide;
